import React, { useState, useRef } from 'react';
import { ChevronLeft, Trash2, Camera, Sparkles, ArrowLeftRight, CalendarPlus, RefreshCw, Check, Bookmark, Pencil } from 'lucide-react';
import ProductAnalysisButton from './ProductAnalysisButton';
import { useAuth } from '../hooks/useAuth';
import CatalogProductImage from './CatalogProductImage';

// Shelf usage tags (muted/warm — Sofia 2026-06-30). Keep in sync with the picker + catalog.
const USAGE_TAGS = [
  { key: 'in_rotation', label: 'In rotation', bg: '#EAF1EA', fg: '#5C7A57' },
  { key: 'now_and_then', label: 'Now & then', bg: '#FBF1E4', fg: '#8A5A2A' },
  { key: 'seasonal', label: 'Seasonal', bg: '#EAEFF5', fg: '#51708F' },
  { key: 'for_concerns', label: 'For concerns', bg: '#F5EAEA', fg: '#9A5A5A' },
];
const TAG_BY_KEY = Object.fromEntries(USAGE_TAGS.map((t) => [t.key, t]));

// Finish lives in the shade prefix ("[Blurry] Silent") — surface name + finish separately.
const splitShade = (raw) => {
  const s = String(raw || '').trim();
  const m = s.match(/^\[([^\]]+)\]\s*(.*)$/);
  if (m) return { name: (m[2] || s).trim(), finish: m[1].trim() };
  return { name: s, finish: '' };
};

// #55: a product reads as makeup when its category says so, it carries a real
// shade, or its name looks like a colour cosmetic (catches cushions/blush/etc.
// with no stored shade — Sofia 2026-07-13).
const MAKEUP_RE = /\b(cushion|foundation|concealer|corrector|bb\s?cream|cc\s?cream|blush(?:er)?|bronzer|highlighter|contour|lipstick|lip\s?tint|lip\s?gloss|lip\s?balm|lip\s?stain|lip\s?liner|mascara|eyeliner|eye\s?liner|eyeshadow|eye\s?shadow|brow|tinted\s?moisturi[sz]er)\b/i;
const isMakeupProduct = (p) => {
  const cat = String(p?.category || p?.product_category || '').toLowerCase();
  if (cat.includes('makeup') || cat.includes('cosmetic')) return true;
  const s = String(p?.recommended_shade || '').trim();
  if (s && s.toUpperCase() !== 'N/A') return true;
  const name = `${p?.name || ''} ${p?.product_name || ''} ${p?.product_type || ''}`;
  return MAKEUP_RE.test(name);
};

const ProductDetail = ({
  product, sourceListId, onBack, onSave, onDelete, onAnalysisComplete,
  onCompare, onAddToRoutine, onReMatch, userId,
}) => {
  const { getAuthSession } = useAuth();
  const imageInputRef = useRef(null);
  const [p, setP] = useState(product);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [editingTags, setEditingTags] = useState(false);
  const [noteDraft, setNoteDraft] = useState(product.notes || '');
  const [showAnalysis, setShowAnalysis] = useState(false);

  const heroSrc = p.image || p.catalog_image_url || p.image_base64;
  const makeup = isMakeupProduct(p);
  const { name: shadeName, finish } = splitShade(p.recommended_shade);
  const tags = Array.isArray(p.usage_tags) ? p.usage_tags : [];
  const analysis = p.ai_analysis && typeof p.ai_analysis === 'object' ? p.ai_analysis : null;

  // #55: attribute/benefit chips. Skincare → benefits; makeup → practical attributes
  // (finish/coverage/SPF) plus any benefit. All optional — the row hides if empty,
  // so today's data degrades gracefully until the backend emits these fields.
  const benefits = (Array.isArray(p.benefits) ? p.benefits
    : Array.isArray(analysis?.benefits) ? analysis.benefits : []).filter(Boolean);
  const mkFinish = p.finish || analysis?.finish;
  const mkCoverage = p.coverage || analysis?.coverage;
  const mkSpf = p.spf || analysis?.spf;
  const attrChips = [];
  if (makeup) {
    if (mkFinish) attrChips.push(String(mkFinish));
    if (mkCoverage) attrChips.push(`${mkCoverage}${/coverage/i.test(String(mkCoverage)) ? '' : ' coverage'}`);
    if (mkSpf) attrChips.push(`SPF ${String(mkSpf).replace(/^spf\s*/i, '')}`);
  }
  const chips = makeup ? [...attrChips, ...benefits] : benefits;

  // Key ingredients: prefer structured {name, benefit} rows; fall back to the string.
  const ingredientRows = (() => {
    const src = Array.isArray(p.ingredients) ? p.ingredients
      : Array.isArray(analysis?.ingredients) ? analysis.ingredients : [];
    return src.filter((i) => i && (i.name || i.benefit)).map((i) => ({ name: i.name || '', benefit: i.benefit || '' }));
  })();
  const keyIngredientsText = (() => {
    const k = analysis?.key_ingredients;
    if (Array.isArray(k) && k.length) return k.join(', ');
    if (typeof k === 'string') return k;
    return analysis?.ingredients_highlight || '';
  })();
  const ingredientsLabel = makeup ? 'Also good to know' : 'Key ingredients';
  const skinFit = analysis?.reasoning || analysis?.ingredient_insight || '';

  const shadeTrio = (Array.isArray(p.shade_trio) ? p.shade_trio
    : Array.isArray(analysis?.shade_trio) ? analysis.shade_trio : []).filter(Boolean);
  const swatchStyle = {
    backgroundColor: p.swatch_hex || '#E4D2BE',
    backgroundImage: p.swatch_image_url ? `url(${p.swatch_image_url})` : undefined,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  };
  const listCat = p.list_category || sourceListId || 'current';
  const owned = listCat !== 'wishlist';

  const persist = (next) => { setP(next); onSave?.(next); };
  const toggleTag = (key) => {
    const next = tags.includes(key) ? tags.filter((t) => t !== key) : [...tags, key];
    persist({ ...p, usage_tags: next });
  };
  const saveNote = () => { if ((noteDraft || '') !== (p.notes || '')) persist({ ...p, notes: noteDraft }); };

  const handleChangePhoto = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setIsUploadingImage(true);
    try {
      const { userId: uid } = await getAuthSession();
      const { supabase } = await import('../supabase');
      const filePath = `uploaded_images/${uid}/${Date.now()}_${file.name}`;
      const { error: uploadError } = await supabase.storage
        .from('images').upload(filePath, file, { cacheControl: '3600', upsert: false });
      if (uploadError) throw uploadError;
      const { data } = supabase.storage.from('images').getPublicUrl(filePath);
      persist({ ...p, image: data.publicUrl, image_base64: null });
    } catch (err) {
      console.error('Error uploading image:', err);
    } finally {
      setIsUploadingImage(false);
      if (imageInputRef.current) imageInputRef.current.value = '';
    }
  };

  const handleDelete = () => { setShowDeleteConfirm(false); onDelete?.(p.id); };

  return (
    <div className="min-h-full bg-white" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <div className="bg-white">
        {/* Keep a clean hero photo when available; otherwise show the shared illustration. */}
        <div className="relative" style={{ height: 224, background: '#F4F1EC' }}>
          <CatalogProductImage src={heroSrc} alt={p.name} brand={p.brand} className="w-full h-full object-cover" />
          {onBack && (
            <button type="button" onClick={onBack}
              className="absolute top-3 left-3 h-9 w-9 rounded-full bg-white/90 border border-[#E5E0D8] flex items-center justify-center hover:bg-white"
              aria-label="Back">
              <ChevronLeft className="h-5 w-5 text-[#2A1212]" />
            </button>
          )}
          <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={handleChangePhoto} />
          <button type="button" onClick={() => imageInputRef.current?.click()} disabled={isUploadingImage}
            className="absolute bottom-3 right-3 h-9 w-9 rounded-full bg-white/90 border border-[#E5E0D8] flex items-center justify-center hover:bg-white disabled:opacity-50"
            aria-label="Change photo">
            <Camera className="h-4 w-4 text-[#2A1212]" />
          </button>
        </div>

        <div className="p-4">
          {p.brand && <p className="text-[13px] uppercase tracking-wide text-[#2A324B]/55">{p.brand}</p>}
          <p className="text-[19px] font-semibold text-[#2A1212] leading-tight mt-0.5">{p.name}</p>

          {chips.length > 0 && (
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              {chips.map((c, i) => (
                <span key={i} className="text-[11.5px] font-medium rounded-full px-3 py-1.5"
                  style={{ background: '#F5EFE7', color: '#6A5A4C' }}>{c}</span>
              ))}
            </div>
          )}

          {/* Your shade (makeup) */}
          {makeup && shadeName && (
            <div className="mt-4 rounded-2xl border border-[#F0E8DF] bg-[#FBF6F0] p-3.5">
              <div className="flex items-center justify-between">
                <span className="text-[10.5px] uppercase tracking-wide text-[#2A324B]/55">Your shade</span>
                {onReMatch && (
                  <button type="button" onClick={() => onReMatch(p)}
                    className="text-[11.5px] font-medium text-[#3F7355] inline-flex items-center gap-1">
                    <RefreshCw className="h-3 w-3" /> Re-match
                  </button>
                )}
              </div>
              <div className="flex items-center gap-3 mt-2.5">
                <span className="rounded-full flex-none" style={{ width: 48, height: 48, ...swatchStyle, boxShadow: 'inset 0 0 0 1px rgba(42,50,75,0.1)' }} />
                <div>
                  <div className="text-[15px] font-semibold text-[#2A1212]">{shadeName}</div>
                  <div className="text-[11.5px] text-[#8A7A6C] mt-0.5">{finish ? `${finish} · ` : ''}Matched from your selfie</div>
                </div>
              </div>
              {shadeTrio.length >= 2 && (
                <>
                  <div className="h-px bg-[#EEE5DA] my-3" />
                  <div className="flex items-start justify-around">
                    {shadeTrio.map((s, i) => {
                      const match = !!(s.is_match || s.match);
                      const label = s.shade_name || s.name || '';
                      return (
                        <div key={i} className="text-center">
                          <span className="rounded-full inline-block" style={{
                            width: match ? 42 : 36, height: match ? 42 : 36,
                            backgroundColor: s.hex || s.swatch_hex || '#E4D2BE',
                            boxShadow: match ? '0 0 0 2px #fff, 0 0 0 4px #6B9478' : 'inset 0 0 0 1px rgba(42,50,75,0.1)',
                          }} />
                          <div className="text-[10.5px] mt-1.5" style={{ color: match ? '#3F7355' : '#8A7A6C', fontWeight: match ? 600 : 400 }}>
                            {label}{match ? ' · you' : ''}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Key ingredients / Also good to know */}
          {(ingredientRows.length > 0 || keyIngredientsText) && (
            <div className="mt-5">
              <p className="text-[10.5px] uppercase tracking-[0.05em] text-[#A28E7E] mb-2.5">{ingredientsLabel}</p>
              {ingredientRows.length > 0 ? (
                <div className="space-y-3">
                  {ingredientRows.map((ing, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="h-8 w-8 rounded-lg bg-[#F3EDE3] flex items-center justify-center flex-none">
                        <Sparkles className="h-4 w-4 text-[#8A7355]" />
                      </span>
                      <div>
                        <div className="text-[13px] font-medium text-[#2A1212]">{ing.name}</div>
                        {ing.benefit && <div className="text-[11.5px] text-[#8A7A6C] mt-0.5">{ing.benefit}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[13.5px] text-[#2A1212] leading-relaxed">{keyIngredientsText}</p>
              )}
            </div>
          )}

          {/* Why it suits you */}
          {skinFit && (
            <div className="mt-5">
              <p className="text-[10.5px] uppercase tracking-[0.05em] text-[#A28E7E] mb-2">Why it suits you</p>
              <p className="text-[13px] text-[#4A3E36] leading-relaxed">{skinFit}</p>
            </div>
          )}

          <div className="h-px bg-[#F1ECE4] my-4" />

          {/* Extras — shelf tags */}
          <div className="flex items-center justify-between mb-2">
            <span className="text-[12.5px] font-medium text-[#2A1212]">On your shelf as</span>
            <button type="button" onClick={() => setEditingTags((v) => !v)}
              className="text-[11.5px] text-[#8A7A6C] inline-flex items-center gap-1">
              <Pencil className="h-3 w-3" /> Edit
            </button>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {tags.length > 0 ? tags.map((k) => {
              const t = TAG_BY_KEY[k];
              return t ? (
                <span key={k} className="text-[13px] font-medium rounded-full px-2.5 py-1"
                  style={{ background: t.bg, color: t.fg }}>{t.label}</span>
              ) : null;
            }) : (
              <span className="text-[13px] text-[#2A324B]/45">No tags yet</span>
            )}
          </div>
          {editingTags && (
            <div className="flex flex-wrap gap-2 mt-2.5">
              {USAGE_TAGS.map((t) => {
                const on = tags.includes(t.key);
                return (
                  <button key={t.key} type="button" onClick={() => toggleTag(t.key)}
                    className="text-[13px] font-medium rounded-full px-3 py-1.5 transition-colors"
                    style={on
                      ? { background: t.bg, color: t.fg, boxShadow: `inset 0 0 0 1.5px ${t.fg}` }
                      : { background: '#fff', color: '#2A324B', border: '1px solid #E5E0D8' }}>
                    {on ? '✓ ' : ''}{t.label}
                  </button>
                );
              })}
            </div>
          )}

          {/* Extras — note */}
          <div className="mt-4">
            <p className="text-[12.5px] font-medium text-[#2A1212] mb-1.5">Your note</p>
            <textarea value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)} onBlur={saveNote}
              placeholder="Add a note…" rows={2}
              className="w-full text-[13px] text-[#2A1212] bg-[#FBF6F0] rounded-xl px-3 py-2.5 border border-[#F0E8DF] focus:outline-none focus:ring-2 focus:ring-[#F3D5B5] resize-none" />
          </div>

          {/* Primary actions */}
          <div className="flex gap-2.5 mt-5">
            <button type="button" onClick={() => onAddToRoutine?.(p)}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-2xl py-3 text-[13.5px] font-medium"
              style={{ background: '#2A1212', color: '#F4ECE3' }}>
              <CalendarPlus className="h-4 w-4" /> Add to routine
            </button>
            <div className="inline-flex items-center gap-1.5 rounded-2xl px-4 text-[13.5px] font-medium"
              style={owned
                ? { color: '#3F7355', border: '1px solid #CFE0D0' }
                : { color: '#51708F', border: '1px solid #D3DEEA' }}>
              {owned ? <Check className="h-4 w-4" /> : <Bookmark className="h-4 w-4" />}
              {owned ? 'Owned' : 'Wishlist'}
            </div>
          </div>

          {/* Secondary actions */}
          <div className="flex items-center justify-center gap-6 mt-3.5">
            {analysis && (
              <button type="button" onClick={() => setShowAnalysis((v) => !v)}
                className="text-[13px] text-[#8A7A6C] inline-flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5" /> Analysis
              </button>
            )}
            <button type="button" onClick={() => onCompare?.(p)}
              className="text-[13px] text-[#8A7A6C] inline-flex items-center gap-1.5">
              <ArrowLeftRight className="h-3.5 w-3.5" /> Compare
            </button>
            <button type="button" onClick={() => setShowDeleteConfirm(true)}
              className="text-[13px] text-[#B27C63] inline-flex items-center gap-1.5">
              <Trash2 className="h-3.5 w-3.5" /> Remove
            </button>
          </div>

          {!analysis && (
            <div className="mt-3">
              <ProductAnalysisButton product={p} onAnalysisComplete={onAnalysisComplete} userId={userId} compact />
            </div>
          )}

          {analysis && showAnalysis && (
            <div className="mt-4 pt-4 border-t border-[#F1ECE4] space-y-2 text-[13.5px] text-[#2A1212]">
              {typeof analysis.compatibility_score === 'number' && (
                <p className="font-semibold">Match score: {analysis.compatibility_score}/100</p>
              )}
              {analysis.reasoning && <p className="text-[#2A324B]/85">{analysis.reasoning}</p>}
              {Array.isArray(analysis.pros) && analysis.pros.length > 0 && (
                <div>
                  <p className="text-[13px] font-semibold text-[#2A324B]/60 mt-1">Pros</p>
                  <ul className="list-disc pl-5">{analysis.pros.map((x, i) => <li key={i}>{x}</li>)}</ul>
                </div>
              )}
              {Array.isArray(analysis.concerns) && analysis.concerns.length > 0 && (
                <div>
                  <p className="text-[13px] font-semibold text-[#2A324B]/60 mt-1">Watch-outs</p>
                  <ul className="list-disc pl-5">{analysis.concerns.map((x, i) => <li key={i}>{x}</li>)}</ul>
                </div>
              )}
              <p className="text-[13px] text-[#2A324B]/45 pt-1">Saved analysis — not re-run.</p>
            </div>
          )}
        </div>
      </div>

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            <h3 className="text-[16px] font-semibold text-[#2A1212] mb-2">Remove from shelf?</h3>
            <p className="text-[13px] text-[#2A324B]/70 mb-4">This removes "{p.name}" from your shelf. You can add it again later.</p>
            <div className="flex gap-2">
              <button type="button" onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2.5 border border-[#E5E0D8] text-[#2A1212] rounded-xl text-[13px]">Cancel</button>
              <button type="button" onClick={handleDelete}
                className="flex-1 px-4 py-2.5 bg-[#F5EAEA] text-[#9A5A5A] rounded-xl text-[13px] font-medium">Remove</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
